import React, { Fragment, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect, useSelector } from 'react-redux';
import get from 'lodash/get';
import classnames from 'classnames';
import { equalsIgnoreCase } from 'utils/common';
import {
  Button,
  SuccessBlock,
  Radio,
  Input,
  H1,
  Div,
  P,
  F,
  I,
} from 'components/Ui';
import { RadioGroup } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import PropTypes from 'prop-types';
import { selectSelectedServiceLocation } from 'containers/PrimoAccount/selectors';
import { dataLayerPush } from 'utils/tracking';
import { translateData } from 'utils/translation';
import {
  selectCustomerNotes,
  selectDeliveryNotes,
  selectCustomerNotesUpdatingSuccess,
} from '../selectors';
import styles from './styles.scss';

export const checkIsCustomAnswer = (note, noteValues) =>
  note && noteValues ? !noteValues.find((a) => a.note === note) : false;

const CustomerNotesForm = ({
  onClose,
  onSubmit,
  serviceLocation,
  deliveryNotes,
  customerNotes,
}) => {
  const selectedDeliveryLocation = customerNotes.serviceLocations.find(
    (a) => a.id === serviceLocation.id,
  );
  let existingAdditionalInstructions;
  let existingNote;

  if (selectedDeliveryLocation) {
    existingAdditionalInstructions =
      selectedDeliveryLocation &&
      selectedDeliveryLocation.additionalInstructions
        ? selectedDeliveryLocation.additionalInstructions
        : null;

    existingNote =
      selectedDeliveryLocation && selectedDeliveryLocation.note
        ? selectedDeliveryLocation.note
        : null;
  }

  const [locationType, setLocationType] = useState(
    selectedDeliveryLocation && selectedDeliveryLocation.noteType
      ? selectedDeliveryLocation.noteType
      : deliveryNotes[0].noteType,
  );

  const [
    showAdditionalInstructionsInput,
    setShowAdditionalInstructionsInput,
  ] = useState(!!existingAdditionalInstructions);

  const existingNoteType = deliveryNotes.find((deliveryNote) =>
    equalsIgnoreCase(deliveryNote.noteType, locationType),
  );

  const isCustomAnswer = checkIsCustomAnswer(
    existingNote,
    existingNoteType.noteValues,
  );

  const [locationDescription, setLocationDescription] = useState(
    isCustomAnswer ? 'OTHER' : existingNote,
  );

  const [otherText, setOtherText] = useState(
    isCustomAnswer ? existingNote : '',
  );
  const [additionalInstructions, setAdditionalInstructions] = useState(
    existingAdditionalInstructions,
  );

  const isFormDisabled =
    (locationDescription === 'OTHER' && !otherText) ||
    !locationDescription ||
    !locationType;
  const isCustomerNotesUpdateSuccessful = useSelector(
    selectCustomerNotesUpdatingSuccess(),
  );

  const additionalInstructionsCount = get(additionalInstructions, 'length', 0);

  return (
    <Fragment>
      {!isCustomerNotesUpdateSuccessful ? (
        <ValidatorForm onSubmit={() => {}}>
          <div className={styles.container}>
            <H1>Leave your RSR delivery instructions</H1>
            <Div className={styles.title}>What type of location?</Div>
            <RadioGroup
              name={'location-type'}
              className={styles.radioGroup}
              value={locationType}
              onChange={(e) => {
                setLocationType(e.target.value);
                setLocationDescription(null);
                setOtherText('');
              }}
              aria-label={translateData('What type of location?')}
              row
            >
              {deliveryNotes.map((note) => (
                <Radio
                  size="large"
                  className={styles.radio}
                  value={note.noteType}
                  label={note.noteType.toLowerCase()}
                />
              ))}
            </RadioGroup>
            <Div className={styles.title}>
              Where should we leave your order?
            </Div>
            <RadioGroup
              name={'location-description'}
              className={styles.radioGroup}
              value={locationDescription}
              onChange={(e) => {
                setLocationDescription(e.target.value);
                setOtherText('');
              }}
              aria-label={translateData('What type of location?')}
            >
              {existingNoteType.noteValues
                .sort((a, b) => {
                  if (a.note === 'OTHER') return 1;
                  if (b.note === 'OTHER') return -1;

                  return a.note > b.note ? -1 : 1;
                })
                .map((value) => {
                  if (
                    value.note === 'OTHER' &&
                    value.note === locationDescription
                  ) {
                    return (
                      <Radio
                        size="large"
                        className={styles.radio}
                        classes={{
                          label: styles.radioLabel,
                        }}
                        value={value.note}
                        label={
                          <Input
                            placeholder="Enter location"
                            style={{ marginBottom: 9, width: '100%' }}
                            value={otherText}
                            maxLength={70}
                            onChange={(e) => setOtherText(e.target.value)}
                          />
                        }
                      />
                    );
                  }

                  return (
                    <Radio
                      size="large"
                      className={styles.radio}
                      value={value.note}
                      label={value.description}
                    />
                  );
                })}
            </RadioGroup>
            <Div className={styles.title}>Additional Instructions</Div>
            {showAdditionalInstructionsInput ? (
              <Fragment>
                <P>
                  If you have a security code, trick gate, or any other
                  important delivery instructions, let us know.{' '}
                  <I>
                    Please do not leave product orders, equipment orders, or
                    order edits in your delivery instructions.
                  </I>
                </P>
                <Input
                  multiline
                  rows={8}
                  value={additionalInstructions}
                  onChange={(e) => setAdditionalInstructions(e.target.value)}
                  name="additionalInstructions"
                  id="additionalInstructions"
                  ariaLabel="Additional Instructions"
                  minHeight={165}
                  required={false}
                />
                <p
                  className={classnames(styles.count, {
                    [styles.countOver]: additionalInstructionsCount > 140,
                  })}
                >
                  <F>Characters</F> {additionalInstructionsCount}{' '}
                  <F>of 140 maximum entered</F>
                </p>
                <P className={styles.message}>
                  We will do our best to follow your instructions but we are
                  unable to guarantee it.
                </P>
              </Fragment>
            ) : (
              <Button
                link
                bold
                text="Add delivery instructions"
                onClick={() => setShowAdditionalInstructionsInput(true)}
              />
            )}
          </div>
          <div className={styles.footer}>
            <div className={styles.buttons}>
              <Button
                color="gray"
                className={styles.button}
                text="Cancel"
                onClick={onClose}
                type="button"
              />
              <Button
                className={styles.button}
                text="Save"
                disabled={isFormDisabled || additionalInstructionsCount > 140}
                onClick={() => {
                  onSubmit({
                    locationDescription,
                    locationType,
                    additionalInstructions,
                    otherText,
                  });
                  dataLayerPush(
                    'SelfServe',
                    {
                      event: 'edit-delivery-instructions',
                      type: locationType,
                      location: locationDescription,
                    },
                    'dlS38',
                  );
                }}
              />
            </div>
          </div>
        </ValidatorForm>
      ) : (
        <div className={styles.successContainer}>
          <SuccessBlock text="Your changes have been saved." />
        </div>
      )}
    </Fragment>
  );
};

CustomerNotesForm.propTypes = {
  onClose: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  customerNotes: selectCustomerNotes(),
  deliveryNotes: selectDeliveryNotes(),
  serviceLocation: selectSelectedServiceLocation(),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerNotesForm);
